import { createContext, type ComponentChildren } from 'preact';
import { useMemo, useContext } from 'preact/hooks';
import { type PLPRoute } from '@/domain/Route';
import type { ProductTechnology } from '@/domain/Product';

export type UseRoute = PLPRoute & {
  slug: string;
  inFavorites: boolean;
  inPLP: boolean;
  inPDP: boolean;
};

const RouteContext = createContext<UseRoute | undefined>(undefined);

export type RouteProviderProps = UseRoute & {
  children: ComponentChildren;
};

export function isPathNamePDP(
  pathName: string,
  technology: ProductTechnology,
  productTypePath: string
): boolean {
  if (
    typeof window === 'undefined' ||
    productTypePath === undefined ||
    technology === undefined
  ) {
    return false;
  }

  const regex = new RegExp(
    `/${productTypePath.toLowerCase()}/${technology.toLowerCase()}/[^/]+`,
    'i'
  );

  const inPDP = regex.test(pathName);

  return inPDP;
}

export function isPathNamePLP(
  pathName: string,
  technology: ProductTechnology,
  productTypePath: string
): boolean {
  if (
    typeof window === 'undefined' ||
    productTypePath === undefined ||
    technology === undefined
  ) {
    return false;
  }

  const regex = new RegExp(`/${productTypePath}/${technology}$`, 'i');

  const inPLP = regex.test(pathName);

  return inPLP;
}

export function RouteProvider({ children, ...props }: RouteProviderProps) {
  const {
    technology,
    productTypePath
  }: { technology: ProductTechnology; productTypePath: string } = props;

  const inFavorites =
    typeof window !== 'undefined' &&
    window?.location?.pathname?.indexOf('/favorites') !== -1;

  const inPLP =
    typeof window !== 'undefined' &&
    isPathNamePLP(window?.location?.pathname, technology, productTypePath);

  const inPDP =
    typeof window !== 'undefined' &&
    isPathNamePDP(window?.location?.pathname, technology, productTypePath);

  const contextValue = useMemo(
    () => ({ ...props, inFavorites, inPLP, inPDP, slug: props.slug || '' }),
    [props, inFavorites, inPLP, inPDP, props.slug]
  );

  return (
    <RouteContext.Provider value={contextValue}>
      {children}
    </RouteContext.Provider>
  );
}

export default function useRoute(): UseRoute {
  const context = useContext(RouteContext);

  if (!context) {
    throw new Error('useRoute must be used within RouteProvider');
  }
  return context;
}
